import type {ConnectionEditComponentProps} from '@cohort/merchants/apps';
import ErrorState from '@cohort/merchants/components/ErrorState';
import Header from '@cohort/merchants/components/Header';
import Loader from '@cohort/merchants/components/Loader';
import {connectionsKeys, useConnection} from '@cohort/merchants/hooks/api/Connections';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {notify} from '@cohort/merchants/hooks/toast';
import {getOauthAuthorizationUrl} from '@cohort/merchants/lib/api/Connections';
import type {OAuthErrorMessage, OAuthSuccessMessage} from '@cohort/merchants/lib/Messages';
import {useMessageListener} from '@cohort/merchants/lib/Messages';
import {getOauthRedirectUrl} from '@cohort/merchants/lib/Utils';
import {notifyError} from '@cohort/merchants/stores/ErrorModalStore';
import {InstagramMerchantConnectorSpec} from '@cohort/shared/schema/apps/instagram/merchantConnector';
import {useQueryClient} from '@tanstack/react-query';
import {Fragment, useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {match} from 'ts-pattern';

const generateOauthPopupFeatures = (): string => {
  const props = {
    popup: 'yes',
    toolbar: 'no',
    menubar: 'no',
    width: 600,
    height: 700,
  };
  const {width, height} = props;
  const {outerHeight, outerWidth} = window;
  const top = Math.round(outerHeight / 2 - height / 2);
  const left = Math.round(outerWidth / 2 - width / 2);
  return Object.entries({top, left, ...props})
    .map(([k, v]) => `${k}=${v}`)
    .join(',');
};

const InstagramEditConnection: React.FC<ConnectionEditComponentProps> = ({
  existingConnectionId,
  onClose,
}) => {
  const merchant = useCurrentMerchant();
  const queryClient = useQueryClient();
  const [connectionId, setConnectionId] = useState(existingConnectionId);
  const [oauthError, setOauthError] = useState(false);
  const {t} = useTranslation('app-instagram', {
    keyPrefix: 'editConnection',
  });

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const {data: connection} = useConnection(merchant.id, connectionId!, {
    enabled: !!connectionId,
  });

  const onMessageReceived = (message: OAuthSuccessMessage | OAuthErrorMessage): void => {
    switch (message.code) {
      case 'oauth-success':
        queryClient.invalidateQueries(connectionsKeys.list(merchant.id));
        setConnectionId(message.connectionId);
        notify('success', t('connectionSuccessful'));
        break;
      case 'oauth-error':
        onClose();
        notify('error', t('connectionError'));
        setOauthError(true);
        break;
    }
  };

  useMessageListener(['oauth-success', 'oauth-error'], onMessageReceived);

  const startOauthFlow = useCallback(async () => {
    try {
      const response = await getOauthAuthorizationUrl(merchant.id, {
        connectorId: InstagramMerchantConnectorSpec.id,
        adminRedirectUrl: getOauthRedirectUrl(),
      });
      // Open a popup for the user to authorize the connection
      window.open(response.authorizationUrl, '_blank', generateOauthPopupFeatures());
    } catch (err) {
      // Show a toast and close
      notifyError(err);
      onClose();
    }
  }, [merchant.id, onClose]);

  useEffect(() => {
    if (connectionId === undefined) {
      startOauthFlow();
    }
  }, [connectionId, startOauthFlow]);

  useEffect(() => {
    if (connection && connection.status === 'ready') {
      onClose();
    }
  }, [connection, onClose]);

  const content = match({connection, oauthError})
    .with({connection: undefined}, () => <Loader />)
    .with({oauthError: true}, () => <ErrorState />)
    .otherwise(() => <Fragment />);

  return (
    <div className="flex flex-col items-center justify-center space-y-4 p-6">
      <Header title={t('title')} subtitle={t('subtitle')} />
      {content}
    </div>
  );
};

export default InstagramEditConnection;
